import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ThemeLogo from './ThemeLogo';
import { Link } from 'react-router-dom';
import {
  confirmResetPassword,
  resetPassword,
  signIn,
  signOut,
} from '@aws-amplify/auth';

import './stylesheet/customLogin.css';

function ForgotPassword() {
  const [step, setStep] = useState('enterEmail'); // 'enterEmail' | 'enterCode'
  const [resetUserName, setResetUserName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClose = () => setOpenError(false);

  const showError = (msg) => {
    setOpenError(true);
    setErrorMessage(msg);
    setLoading(false);
  };

  const handleSendCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!resetUserName.trim()) {
      showError('Please enter your email or username.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ username: resetUserName });
      setStep('enterCode');
      setLoading(false);
    } catch (error) {
      showError(error.message);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await resetPassword({ username: resetUserName });
      setLoading(false);
    } catch (error) {
      showError(error.message);
    }
  };

  const handleSubmitNewPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      await confirmResetPassword({
        username: resetUserName,
        confirmationCode: code,
        newPassword,
      });
      const { isSignedIn } = await signIn({
        username: resetUserName,
        password: newPassword,
      });
      if (isSignedIn) {
        await signOut();
        window.location.href = '/';
      }
    } catch (error) {
      if (error.name === 'UserLambdaValidationException') {
        showError('This email is already being used by another account');
      } else {
        showError(error.message);
      }
    }
  };

  const handleFormSubmission = (e) => {
    if (step === 'enterEmail') {
      handleSendCode(e);
    } else {
      handleSubmitNewPassword(e);
    }
  };

  return (
    <div className="mainDiv">
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openError}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
      <form onSubmit={handleFormSubmission}>
        {step === 'enterEmail' && (
          <>
            <Typography component="div">
              <div>
                <ThemeLogo />
              </div>
              <h4>Reset your password</h4>
            </Typography>
            <div className="inputArea">
              <p className="inputDescription">
                Enter the email or username of the account you want to recover
              </p>
              <TextField
                id="resetUserName"
                key="resetUserName"
                name="resetUserName"
                style={{ width: '100%' }}
                placeholder="Enter your account email here"
                variant="outlined"
                type="text"
                value={resetUserName}
                onChange={(e) => setResetUserName(e.target.value)}
              />
              <p className="inputDescription">
                We will send a password reset code to the email connected to this account
              </p>
            </div>
            <div className="actionArea">
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Link to="/">
                  <Button
                    style={{ marginRight: '10px', textDecoration: 'none' }}
                    className="backButton"
                    variant="outlined"
                    color="info"
                  >
                    Back
                  </Button>
                </Link>
                <Button
                  className="resetButton"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  Send Code
                  {loading && <CircularProgress size={24} className="buttonProgress" />}
                </Button>
              </div>
            </div>
          </>
        )}
        {step === 'enterCode' && (
          <>
            <Typography component="div">
              <div>
                <ThemeLogo />
              </div>
              <h4>Reset your password</h4>
            </Typography>
            <div className="inputArea">
              <p className="inputDescription">Reset code</p>
              <TextField
                id="code"
                key="code"
                name="code"
                style={{ width: '100%' }}
                placeholder="Enter the password reset code sent to your email"
                variant="outlined"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <p className="inputDescription">New Password</p>
              <TextField
                style={{ width: '100%' }}
                placeholder="Enter your new password"
                variant="outlined"
                id="newPassword"
                key="newPassword"
                name="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="s-text noWrap">
                Must contain 6+ characters, special character(s), number(s), uppercase and
                lower case letter(s).
              </p>
              <p className="inputDescription" style={{ marginTop: '8px' }}>
                After submitting your new password, you will be redirected back to the sign in
                page
              </p>
            </div>
            <div className="actionArea">
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Link to="/">
                  <Button
                    style={{ marginRight: '10px' }}
                    className="backButton"
                    variant="outlined"
                    color="info"
                  >
                    Back
                  </Button>
                </Link>
                <Button
                  style={{ marginRight: '10px' }}
                  className="backButton"
                  variant="outlined"
                  color="info"
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  Resend Code
                  {loading && <CircularProgress size={24} className="buttonProgress" />}
                </Button>
                <Button
                  className="resetButton"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  Submit
                  {loading && <CircularProgress size={24} className="buttonProgress" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default ForgotPassword;
